import { useEffect, useMemo, useState } from "react";
import { Star } from "lucide-react";
import { api } from "../lib/api";
import ReviewCard from "../components/ReviewCard";

export default function Reviews() {
    const [reviews, setReviews] = useState([]);
    const [minRating, setMinRating] = useState(0);

    useEffect(() => {
        api.get("/reviews").then((r) => setReviews(r.data));
    }, []);

    const filtered = useMemo(
        () => reviews.filter((r) => r.rating >= minRating),
        [reviews, minRating]
    );

    const avg = reviews.length
        ? (reviews.reduce((a, r) => a + r.rating, 0) / reviews.length).toFixed(1)
        : "0.0";

    const buckets = [5, 4, 3, 2, 1].map((n) => ({
        n,
        count: reviews.filter((r) => r.rating === n).length,
    }));
    const total = reviews.length || 1;

    return (
        <>
            <section className="relative overflow-hidden">
                <div className="hero-glow" />
                <div className="relative max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7">
                        <div className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold">Reviews</div>
                        <h1 className="mt-3 font-display font-extrabold text-slate-900 text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                            What Glendale says about EG Wireless.
                        </h1>
                        <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl">
                            Honest, unedited feedback from real customers — across smartphones, activations, accessories and tech support.
                        </p>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="bg-white rounded-3xl border border-slate-100 premium-shadow p-8">
                            <div className="flex items-baseline gap-3">
                                <div className="font-display font-extrabold text-5xl text-slate-900">{avg}</div>
                                <div className="text-slate-500">/ 5.0</div>
                            </div>
                            <div className="flex items-center gap-1 mt-2">
                                {Array.from({ length: 5 }).map((_, i) => (
                                    <Star key={i} className={`w-5 h-5 ${i < Math.round(avg) ? "fill-yellow-400 text-yellow-400" : "text-slate-200"}`} />
                                ))}
                                <span className="ml-2 text-sm text-slate-500">{reviews.length} reviews</span>
                            </div>
                            <div className="mt-6 space-y-2.5">
                                {buckets.map(({ n, count }) => (
                                    <div key={n} className="flex items-center gap-3 text-sm">
                                        <div className="text-slate-700 font-medium w-3">{n}</div>
                                        <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
                                        <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                                            <div className="h-full bg-slate-900 rounded-full" style={{ width: `${(count / total) * 100}%` }} />
                                        </div>
                                        <div className="text-slate-500 w-8 text-right">{count}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Filter */}
            <section className="max-w-7xl mx-auto px-5 lg:px-10">
                <div className="flex flex-wrap gap-2">
                    {[
                        [0, "All"],
                        [5, "5 stars"],
                        [4, "4+ stars"],
                        [3, "3+ stars"],
                    ].map(([n, l]) => (
                        <button
                            key={n}
                            data-testid={`review-filter-${n}`}
                            onClick={() => setMinRating(n)}
                            className={`px-4 h-10 rounded-full text-sm font-medium transition ${
                                minRating === n
                                    ? "bg-slate-900 text-white"
                                    : "bg-white border border-slate-200 text-slate-700 hover:border-slate-400"
                            }`}
                        >
                            {l}
                        </button>
                    ))}
                </div>
            </section>

            {/* Grid */}
            <section data-testid="reviews-grid" className="max-w-7xl mx-auto px-5 lg:px-10 mt-10 pb-20 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filtered.map((r, i) => (
                    <ReviewCard key={r.id} review={r} dataTestId={`review-card-${i}`} />
                ))}
            </section>
        </>
    );
}
